import { GridCell } from '@Common/Controls/SimpleGridLight/GridCell';
import { ColumnCollection } from './ColumnCollection';
import { NullGridColumnEditor } from './Editors/GridEditors';
import { JQueryHelper } from './JQueryHelper';
import { classNames, ColumnWidth, ColumnWidthType, IDynamicSetting, IGridColumnProperties, RenderArg, SortOrder } from './Settings';
import { IGridColumnEditor } from './SimpleGridLight';
import { SortManager } from './SortManager';
import { enumerable } from '@Common/Core/CoreDecorator';

const defaultGridColumnSetting = {
    isSortable: true,
    sortOrder: SortOrder.None,
    css: {},
    cellCss: {},
    width: { type: ColumnWidthType.byTitleContent },
    className: '',
    cellClassName: ''
};

type ColumnSettingWithDefault = RequiredKeys<IGridColumnProperties, keyof typeof defaultGridColumnSetting | 'editor'>;

export class GridColumn {
    private readonly prop: ColumnSettingWithDefault;
    private readonly columnNum: number;
    private readonly _tableColumn: JQuery;
    private readonly bodyColumn: JQuery;
    private readonly widthDef: ColumnWidth;
    private readonly own: ColumnCollection;
    private readonly minColumnWidth = 20;

    constructor(tableColumn: JQuery, bodyColumn: JQuery, prop: IGridColumnProperties, colNum: number, own: ColumnCollection) {
        this.prop = _.defaults(prop, { editor: new NullGridColumnEditor() }, defaultGridColumnSetting);
        this.widthDef = new ColumnWidth(this.prop.width);

        this.own = own;
        this.columnNum = colNum;
        this._tableColumn = tableColumn;
        this.bodyColumn = bodyColumn;

        this.addClass(this.prop.className);
        this.tableColumn().css(this.prop.css);
        this.bodyColumn.css(this.prop.css);

        if (this.isSortable()) {
            this.addClass(classNames.sortableColumn);
        }
        this.sortOrder(this.prop.sortOrder);
        this.readonly(this.prop.readonly);

        this.tableColumn().attr('title', JQueryHelper.visibilityText(this.tableColumn()));
    }

    public getComparer(): (x: any, y: any) => number {
        return this.prop.compare;
    }

    public getWidthDef(): ColumnWidth {
        return this.widthDef;
    }

    public dataType(): any {
        return this.prop.dataType;
    }

    public dataIndex(): any {
        return this.prop.dataIndex;
    }

    public columnIndex(): number {
        return this.columnNum;
    }

    public className(): string {
        return this.prop.className;
    }

    public css(): any {
        return this.prop.css;
    }

    public cellClassName(): string {
        return this.prop.cellClassName;
    }

    public cellCss(): any {
        return this.prop.cellCss;
    }

    public isSortable(sortable?: boolean): boolean {
        if (sortable === undefined) {
            return this.prop.isSortable;
        } else {
            this.prop.isSortable = sortable;
            if (!sortable) {
                this.sortOrder(SortOrder.None);
            }
        }
    }

    public sortOrder(sort?: SortOrder): SortOrder {
        if (sort === void 0) {
            return this.prop.sortOrder;
        }

        // TODO move to SM
        this.removeClass(SortManager.getSortClassName(this.prop.sortOrder));
        this.addClass(SortManager.getSortClassName(sort));
        this.prop.sortOrder = sort;

        return this.prop.sortOrder;
    }

    public render(): (arg: RenderArg) => string | JQuery {
        return this.prop.render;
    }

    public getTitle(): (arg: RenderArg) => any {
        return this.prop.getTitle;
    }

    public editor(): IGridColumnEditor {
        const editor = this.prop.editor;
        if (editor.init) {
            editor.init(this);
        }
        return editor;
    }

    public title(title?: string): string {
        if (void 0 === title) {
            return this.prop.title;
        } else {
            return (this.prop.title = title);
        }
    }

    public addClass(className: string): void {
        this._tableColumn.addClass(className);
        this.bodyColumn.addClass(className);
    }

    public removeClass(className: string): void {
        this._tableColumn.removeClass(className);
        this.bodyColumn.removeClass(className);
    }

    public tableColumn(): JQuery {
        return this._tableColumn;
    }

    public width(width?: number): number {
        if (width === void 0) {
            return this.tableColumn().outerWidth();
        }
        if (width >= 0) {
            this.tableColumn().outerWidth(width);
            this.bodyColumn.outerWidth(width);
            if (this.own.sizingLogging) {
                this.bodyColumn.text(width);
            } else {
                this.bodyColumn.text('');
            }
        } else {
            this.tableColumn().width('auto');
            this.bodyColumn.width('auto');
        }
        return width;
    }

    public readonly(value?: boolean): boolean {
        return this.setterImpl(classNames.readonly, col => this.own.setReadonly(col), false, value);
    }

    private setterImpl(className: string, fn: (gc: GridColumn) => void, revert = false, value?: boolean): boolean {
        const check: (b: boolean) => boolean = revert ? b => !b : b => b;

        if (value === void 0) {
            return check(this.tableColumn().hasClass(className));
        }
        if (check(value)) {
            this.addClass(className);
        } else {
            this.removeClass(className);
        }
        fn(this);
        return value;
    }

    public setValue(value: any): void {
        this.own.setValue(this, value);
    }

    public getAccessor(): { get?; set? } {
        return this.prop.dataAccessor;
    }

    public getDynamicSetting(): IDynamicSetting {
        return this.prop.setting;
    }

    public setDynamicSetting(setting: IDynamicSetting) {
        this.prop.setting = {
            ...(this.prop.setting ?? {}),
            ...setting
        };
    }

    public getValidator(): (cell: GridCell) => string[] {
        return this.prop.validator;
    }

    public setUserWidth(width: number): void {
        const cw = this.getWidthDef();
        cw.calcValue = width;
        cw.desired = width;
        cw.type = ColumnWidthType.none;
        delete cw.max;
        this.width(width);
    }

    @enumerable
    public get minWidth(): number {
        const cssProp = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth'];
        return (
            _(cssProp)
                .map(prop => Math.max(getNumericCss(this._tableColumn, prop), getNumericCss(this.bodyColumn, prop)))
                .sum() || this.minColumnWidth
        );
    }

    public validate(cell: GridCell): void {
        if (this.prop.validateByColumn) {
            this.own.getColumnCells(cell).forEach(otherCell => otherCell.validate());
        }
    }
}

function getNumericCss(e: JQuery, prop: string): number {
    return parseInt(e.css(prop)) || 0;
}
